import { useState } from 'react';
import { Edit } from 'lucide-react';

import { AcceptType, Dropzone } from '@/components/dropzone';
import { Button } from '@/components/ui/button';
import { useOrgStore } from '@/stores/org-store';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { uploadOrgLogo } from '@/services/org/upload-org-logo';
import { useNotification } from '@/components/notifications';
import { OrgImage } from '@/components/org-image';

export const OrgLogo = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { currentOrg, updateOrg } = useOrgStore();
  const notification = useNotification();

  // Check if we should show logo text (default is true)
  // If false, logo can be wider/elongated
  const showLogoText = currentOrg?.branding?.show_logo_text ?? true;

  const handleUpload = async (files: File[]) => {
    setIsLoading(true);
    uploadOrgLogo({ file: files[0] })
      .then((res) => {
        const logoUrl = res.data?.branding?.logo_url;
        if (logoUrl && currentOrg?.orgid) {
          const updatedBranding = {
            ...currentOrg.branding,
            logo_url: logoUrl,
          };

          updateOrg(currentOrg.orgid, {
            branding: updatedBranding,
          });

          setIsEditMode(false);
          notification.success('Logo uploaded successfully');
        } else {
          notification.error('Failed to upload logo');
        }
      })
      .catch((error: Error) => {
        console.error(error);
        notification.error('Failed to upload organization logo');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Card>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex justify-between w-full'>
          <div
            id='org-settings-logo-label'
            className='text-zinc-900 dark:text-zinc-50'
          >
            Organization logo
          </div>
          <div className='flex gap-2'>
            {isEditMode ? (
              <Button
                id='org-settings-logo-cancel'
                disabled={isLoading}
                onClick={() => setIsEditMode(false)}
                variant='ghost'
              >
                {isLoading ? <LoadingSpinner /> : 'Cancel'}
              </Button>
            ) : (
              <Button
                id='org-settings-logo-edit'
                onClick={() => setIsEditMode(true)}
                variant='ghost'
                size='icon'
              >
                <Edit />
              </Button>
            )}
          </div>
        </div>
        {isEditMode ? (
          <Dropzone
            handleUpload={handleUpload}
            isLoading={isLoading}
            acceptType={AcceptType.IMAGE}
            multiple={false}
          />
        ) : (
          <div
            className={`flex items-center justify-center h-10 ${
              !showLogoText ? 'w-fit' : 'w-10'
            }`}
          >
            <OrgImage
              imageUrl={currentOrg?.branding?.logo_url}
              className={`h-10 rounded-md ${
                !showLogoText
                  ? 'w-auto object-contain max-w-none'
                  : 'w-10 object-cover'
              }`}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
